import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ProductType } from "@obg/core.config";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import { fromMillisecondsToMinutes } from "@obg/ngx.global";
import { of, timer } from "rxjs";
import { catchError, filter, map, switchMap, take, takeUntil } from "rxjs/operators";

import { APP_CONTEXT_ACTION_TYPE } from "../../app-context/app-context.action";
import { AppContextSelector } from "../../app-context/app-context.selector";
import { AppError } from "../../app-error/app-error.model";
import { AUTH_ACTION_TYPE } from "../../auth/auth.action";
import { AuthSelector } from "../../auth/auth.selector";
import { CoreState } from "../../state";
import { WalletService } from "../../wallet/wallet.service";

import { REALITY_CHECK_ACTION_TYPE, RealityCheckAction } from "./reality-check.action";
import { RealityCheckConfigService } from "./reality-check.config";
import { RealityCheckSelector } from "./reality-check.selector";
import { RealityCheckService } from "./reality-check.service";

@Injectable()
export class RealityCheckEffect {
	realityCheckDisabled$ = createEffect(() =>
		this.config.getIsRealityCheckEnabled$().pipe(
			filter((isEnabled) => !isEnabled),
			map(() => this.action.stopTimer())
		)
	);

	sessionEnd$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => this.action.stopTimer())
		)
	);

	disableRealityCheck$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REALITY_CHECK_ACTION_TYPE.disableRealityCheck),
			map(() => {
				this.service.disableRealityCheck();
				return this.action.stopTimer();
			})
		)
	);

	sessionStart$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionStart),
			filter(() => this.service.getEnabledStatus()),
			concatLatestFrom(() => this.store.select(AppContextSelector.getProduct)),
			filter(
				([, currentProduct]) =>
					this.config.isRealityCheckTimerStartedFromLogin() ||
					this.service.isRealityCheckActiveForProduct(currentProduct)
			),
			map(() => this.action.startTimer())
		)
	);

	productChange$ = createEffect(() =>
		this.actions$.pipe(
			ofType(APP_CONTEXT_ACTION_TYPE.setProduct),
			filter(() => this.service.getEnabledStatus() && !this.config.isRealityCheckTickingRegardlessOfProduct()),
			map(({ payload }: ActionWithPayload<ProductType>) => payload),
			concatLatestFrom(() => [
				this.store.select(AppContextSelector.getPreviousProduct),
				this.store.select(AuthSelector.isAuthenticated),
			]),
			filter(([currentProduct, previousProduct, isAuthenticated]) => {
				if (this.service.shouldTriggerStopForProductChange(currentProduct, previousProduct)) {
					this.store.dispatch(this.action.stopTimer());
					return false;
				}

				return (
					isAuthenticated && this.service.shouldTriggerStartForProductChange(currentProduct, previousProduct)
				);
			}),
			map(() => this.action.startTimer())
		)
	);

	startTimer$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REALITY_CHECK_ACTION_TYPE.startTimer),
			filter(() => this.service.getEnabledStatus()),
			switchMap(() =>
				this.store.select(this.selector.nextPulse()).pipe(
					take(1),
					map((nextPulse) => {
						if (!nextPulse) {
							const firstPulseTime = this.config.getRealityCheckFirstPulseTimeMillis();
							this.store.dispatch(this.action.saveNextPulse(firstPulseTime));
							return firstPulseTime;
						}

						return Math.max(0, nextPulse - Date.now()); // continue if already exists
					}),
					switchMap((firstPulseTime) => timer(firstPulseTime, this.config.getRealityCheckIntervalMillis())),
					map(() =>
						batchActions(
							this.action.timerPulse(),
							this.action.saveNextPulse(this.config.getRealityCheckIntervalMillis())
						)
					),
					takeUntil(this.actions$.pipe(ofType(REALITY_CHECK_ACTION_TYPE.stopTimer)))
				)
			)
		)
	);

	timerPulse$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REALITY_CHECK_ACTION_TYPE.timerPulse),
			filter(() => this.service.getEnabledStatus()),
			concatLatestFrom(() => this.store.select(AuthSelector.getSessionElapsedTimeInMillis)),
			map(([, sessionMills]) => Math.floor(fromMillisecondsToMinutes(sessionMills ?? 0))),
			switchMap((elapsedMinutes) => {
				if (!this.config.isRealityCheckBetWinSummaryNeeded()) {
					return of(
						this.action.timerPulseSuccess({
							elapsedMinutes,
						})
					);
				}

				return this.walletService.getBetWinSummary(elapsedMinutes).pipe(
					map((response) =>
						this.action.timerPulseSuccess({
							currencyCode: response.currencyCode,
							netWinning: response.netWinning,
							totalBets: response.totalBets,
							totalWins: response.totalWins,
							elapsedMinutes,
						})
					),
					catchError((error: AppError) => of(this.action.timerPulseFail(error)))
				);
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: RealityCheckAction,
		private service: RealityCheckService,
		private walletService: WalletService,
		private config: RealityCheckConfigService,
		private selector: RealityCheckSelector,
		private store: Store<CoreState>
	) {}
}
